<template>
  <CCard class="mt-4">
    <CCardHeader
      style="background-color: #145654"
      class="text-white d-flex justify-content-between align-items-center"
    >
      <h4 class="mb-0">
        <CIcon name="cil-description" /> {{ $t('ticketDetail.title') }}
      </h4>
      <div>
        <CButton 
          v-if="report && (report.status === 'pending' || (report.status === 'assigned' && !(report.supervisorPart && report.supervisorPart.assignedTechnician)))" 
          color="danger" 
          size="sm" 
          class="mr-2" 
          @click="cancelTicket"
          :disabled="isProcessing"
        >
          <CIcon name="cil-ban" /> {{ $t('ticketDetail.cancelTicketBtn') }}
        </CButton>
        <CButton color="light" size="sm" @click="$emit('close')">
          <CIcon name="cil-x" />
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody class="p-4">
      
      <div class="work-order-timeline my-4">
        <div class="timeline-line" :style="getTimelineStyle()"></div>
        <div
          v-for="step in timelineSteps"
          :key="step.num"
          :class="['timeline-step', { active: getCurrentStep() >= step.num }]"
        >
          <div v-if="step.num === 1 && getCurrentStep() === 1" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/bb3a855a-a6d2-443c-b6a9-523a0a416476/jYe7PDPdCV.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 2 && getCurrentStep() === 2" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/ebf1b394-df01-458b-a3eb-dd0129aceab2/m4Y81rb9Rp.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 3 && getCurrentStep() === 3" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/b1900545-252b-4418-af8e-2f8427524719/v4NfdUTB1W.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div v-if="step.num === 4 && report.status === 'done'" style="position: absolute; top: -50px; left: 50%; transform: translateX(-50%); width: 60px; height: 60px; z-index: 10; pointer-events: none;">
             <dotlottie-player 
               src="https://lottie.host/6078979a-4896-49cf-9f62-964f5bf12ad4/9eGbn1SSa9.lottie" 
               background="transparent"  
               speed="1"  
               style="width: 100%; height: 100%;"  
               loop  
               autoplay>
             </dotlottie-player>
          </div>
          <div class="step-circle"></div>
          <div class="step-label">
            <div class="step-status">{{ step.status }}</div>
            <div class="step-role">{{ step.role }}</div>
          </div>
        </div>
      </div>

      <!-- แสดงกล่องเตือนหากงานนี้เคยถูกช่างปฏิเสธ -->
      <div v-if="report.rejectionPart && report.rejectionPart.reason && report.status === 'pending'" class="alert alert-warning border-warning d-flex align-items-start mb-4 shadow-sm" role="alert">
        <CIcon name="cil-warning" size="xl" class="mr-3 mt-1 text-warning flex-shrink-0" />
        <div style="flex: 1;">
          <h6 class="alert-heading font-weight-bold text-dark mb-1">
            {{ $t('ticketDetail.tech.rejectedAlertTitle') }}
          </h6>
          <div class="text-secondary small mb-2" v-if="getRejectionAuthor()">
            {{ $t('ticketDetail.tech.rejectedByLabel') }} <strong class="text-dark">{{ getRejectionAuthor() }}</strong> 
            <span v-if="report.rejectionPart.rejectedAt">({{ formatDate(report.rejectionPart.rejectedAt) }})</span>
          </div>
          <div class="p-2 bg-white rounded border border-warning text-danger" style="font-size: 0.95rem;">
            <strong>{{ $t('ticketDetail.tech.rejectedReasonLabel') }}</strong> {{ report.rejectionPart.reason }}
          </div>
        </div>
      </div>

      <!-- ให้ supervisor สามารถมอบหมายงานได้เสมอ แม้ว่าจะเป็นผู้แจ้งเองก็ตาม -->
      <div v-if="(report.status === 'pending' || showAssignBox) && !showForwardBox" class="assignment-box p-4 mb-4">
        <h5 class="font-weight-bold text-info mb-3">
            <span v-if="report.status === 'pending'">{{ $t('ticketDetail.assignTechHeading') }}</span>
            <span v-else>{{ $t('ticketDetail.changeTechHeading') }}</span>
        </h5>
        <div class="form-group">
            <label class="lbl">{{ $t('ticketDetail.pickTech') }}</label>
            <select class="form-control mb-3" v-model="selectedTechnicianId">
                <option :value="null" disabled>{{ $t('ticketDetail.placeholderTech') }}</option>
                <option v-for="tech in technicians" :key="tech._id" :value="tech._id">
                    {{ tech.name }} {{ tech.surname }}
                </option>
            </select>
            <CButton 
                color="info" 
                class="text-white px-4 shadow-sm" 
                @click="assignTask"
                :disabled="!selectedTechnicianId || isProcessing"
            >
                <CIcon name="cil-user-follow" /> 
                {{ isProcessing ? $t('ticketDetail.saving') : (report.status === 'pending' ? $t('ticketDetail.confirmAssignTech') : $t('ticketDetail.confirmChangeTechBtn')) }}
            </CButton>
            <CButton 
                v-if="report.status !== 'pending'"
                color="secondary" 
                class="px-4 shadow-sm ml-2" 
                @click="showAssignBox = false"
                :disabled="isProcessing"
            >
                {{ $t('mgmt.cancel') }}
            </CButton>
            <CButton 
                v-if="report.status === 'pending'"
                color="primary" 
                variant="outline"
                class="px-4 shadow-sm ml-2" 
                @click="showForwardBox = true; showAssignBox = false"
                :disabled="isProcessing"
            >
                <CIcon name="cil-share" /> {{ $t('ticketDetail.supervisor.forwardOther') }}
            </CButton>
        </div>
      </div>

      <!-- Forward to other department -->
      <div v-if="showForwardBox" class="assignment-box p-4 mb-4" style="border-left-color: #3399ff !important; border: 1px solid #c8ced3;">
        <h5 class="font-weight-bold text-primary mb-3">
             <span>{{ $t('ticketDetail.supervisor.forwardOtherShort') }}</span>
        </h5>
        <div class="form-group">
            <label class="lbl">{{ $t('ticketDetail.supervisor.pickTargetDept') }}</label>
            <select class="form-control mb-3" v-model="selectedForwardCategoryId">
                <option :value="null" disabled>{{ $t('ticketDetail.supervisor.placeholderDept') }}</option>
                <option v-for="cat in allCategories" :key="cat._id" :value="cat._id" :disabled="isCurrentCategory(cat._id)">
                    {{ cat.departmentName }} <span v-if="isCurrentCategory(cat._id)">{{ $t('ticketDetail.supervisor.yourDeptTag') }}</span>
                </option>
            </select>
            <CButton 
                color="primary" 
                class="text-white px-4 shadow-sm" 
                @click="forwardTicket"
                :disabled="!selectedForwardCategoryId || isProcessing"
            >
                <CIcon name="cil-share" /> 
                {{ isProcessing ? $t('ticketDetail.supervisor.processing') : $t('ticketDetail.supervisor.confirmForward') }}
            </CButton>
            <CButton 
                color="secondary" 
                class="px-4 shadow-sm ml-2" 
                @click="showForwardBox = false; if(report.status !== 'pending') { showAssignBox = true; }"
                :disabled="isProcessing"
            >
                {{ $t('mgmt.cancel') }}
            </CButton>
        </div>
      </div>

      <!-- ประเมินความพึงพอใจ -->
      <!-- กรณี supervisor เป็นผู้แจ้งเอง และ status = done → แสดงฟอร์มประเมิน -->
      <div
        v-if="amIReporter && ['done','welldone'].includes(report.status)"
        class="evaluation-box text-center p-4 mb-4"
      >
        <h5 class="font-weight-bold text-dark mb-2">
          {{ report.status === 'done' ? $t('ticketDetail.evalPlease') : $t('ticketDetail.evalResult') }}
        </h5>
        <div class="star-rating mb-3">
          <span
            v-for="star in 5"
            :key="star"
            class="star"
            :class="{
              filled: star <= (hoverRating || rating),
              disabled: report.status === 'welldone',
            }"
            @mouseover="report.status === 'done' ? (hoverRating = star) : null"
            @mouseleave="report.status === 'done' ? (hoverRating = 0) : null"
            @click="report.status === 'done' ? (rating = star) : null"
          >★</span>
        </div>
        <div v-if="rating > 0" class="mb-3 text-warning font-weight-bold">
          {{ getRatingText(rating) }}
        </div>
        <CButton
          v-if="report.status === 'done'"
          color="success"
          size="lg"
          @click="submitEvaluation"
          :disabled="isSubmittingRating || rating === 0"
          class="px-5 shadow-sm"
        >
          <span v-if="!isSubmittingRating">{{ $t('ticketDetail.evalSubmit') }}</span>
          <span v-else>{{ $t('ticketDetail.saving') }}</span>
        </CButton>
      </div>

      <!-- กรณีปกติ (supervisor เป็นผู้รับงาน ไม่ใช่ผู้แจ้งเอง) -->
      <div
        v-if="!amIReporter && ['done', 'welldone'].includes(report.status)"
        class="evaluation-box text-center p-4 mb-4"
      >
        <h5 class="font-weight-bold text-dark mb-2">
          {{ report.status === 'done' ? $t('ticketDetail.supervisor.waitEvalLine') : $t('ticketDetail.evalResult') }}
        </h5>
        <div v-if="report.status === 'welldone'">
            <div class="star-rating mb-3">
              <span v-for="star in 5" :key="star" class="star" :class="{ filled: star <= rating, disabled: true }">★</span>
            </div>
            <div v-if="rating > 0" class="mb-3 text-warning font-weight-bold">{{ getRatingText(rating) }}</div>
        </div>
      </div>

      <hr class="border-dashed my-4" />

      <div class="mx-3">
        
        <div class="mb-4 p-3 bg-light rounded border">
           <h6 class="font-weight-bold mb-3 text-primary">{{ $t('ticketDetail.assignees') }}</h6>
           <CRow>
             <CCol sm="6">
                <label class="lbl">{{ $t('ticketDetail.supervisorLabelRole') }}</label>
                <div>{{ getSupervisorName() }}</div>
                <small class="text-muted" v-if="report.supervisorPart && report.supervisorPart.dateTimeInformed">
                   {{ $t('ticketDetail.when') }} {{ formatDate(report.supervisorPart.dateTimeInformed) }}
                </small>
             </CCol>
             <CCol sm="6">
                <label class="lbl">{{ $t('ticketDetail.technicianLabelRole') }}</label>
                <div class="text-primary font-weight-bold" style="font-size: 1.1em;">
                   {{ getTechnicianName() }}
                </div>
                <div class="font-weight-bold mt-1" style="color: #fd7e14; font-size: 1rem;" v-if="report.status === 'assigned'">
                   {{ $t('ticketDetail.waitTechAccept') }}
                </div>
                <div class="mt-2" v-if="['pending', 'assigned', 'in_progress'].includes(report.status)">
                    <CButton size="sm" color="warning" variant="outline" @click="toggleAssignBox" v-if="report.status !== 'pending'">
                        <CIcon name="cil-pencil" /> {{ $t('ticketDetail.changeTechShort') }}
                    </CButton>
                    <CButton size="sm" color="primary" variant="outline" class="ml-2" @click="toggleForwardBox">
                        <CIcon name="cil-share" /> {{ $t('ticketDetail.supervisor.forwardOther') }}
                    </CButton>
                </div>
             </CCol>
           </CRow>
        </div>

        <h5 class="font-weight-bold mb-4">{{ $t('ticketDetail.sectionReport') }}</h5>

        <CRow class="mb-2">
          <CCol sm="12">
            <div class="p-3 bg-light border rounded d-flex align-items-center">
              <div class="mr-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width:40px; height:40px;">
                <CIcon name="cil-tag" />
              </div>
              <div>
                <label class="lbl mb-0 text-muted">{{ $t('ticketDetail.ticketNo') }}</label>
                <div class="text-primary font-weight-bold h4 mb-0">
                  {{ report.ticketNumber || '-' }}
                </div>
              </div>
            </div>
          </CCol>
        </CRow>
        
        <div class="mb-4 mt-2">
          <CRow class="mb-3">
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.reporter') }}</label>
              <div class="text-dark">{{ getUserName() }} <span v-if="getOnBehalfOf()" class="text-danger font-weight-bold ml-1" style="font-size: 0.9em;">({{ $t('ticketDetail.onBehalf') }} {{ getOnBehalfOf() }})</span></div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.deptCombined') }}</label>
              <div class="text-dark">{{ getDepartment() }}</div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.phonePersonalShort') }}</label>
              <div class="text-dark">{{ getPersonalPhone() }}</div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.phoneWorkShort') }}</label>
              <div class="text-dark">{{ getOfficePhone() }}</div>
            </CCol>
          </CRow>
          <CRow>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.jobType') }}</label>
              <div class="text-dark">{{ getCategoryName() }}</div>
            </CCol>
            <CCol sm="6" md="3" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.workArea') }}</label>
              <div class="text-dark">{{ getLocationFull() }}</div>
            </CCol>
            <CCol sm="12" md="6" class="mb-3">
              <label class="lbl mb-1">{{ $t('ticketDetail.timeSlot') }}</label>
              <div class="text-dark">{{ getAvailableTime() }}</div>
            </CCol>
          </CRow>
        </div>

        <CRow v-if="isOutdoor()" class="mb-4">
          <CCol>
            <label class="lbl">{{ $t('ticketDetail.mapPoint') }}</label>
            <div class="map-container border rounded shadow-sm overflow-hidden" style="height: 350px;">
              <div ref="mapContainer" style="width: 100%; height: 100%"></div>
            </div>
            <div class="mt-2 text-right">
                <a :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" target="_blank" class="btn btn-sm btn-outline-primary">
                    <CIcon name="cil-map" /> {{ $t('ticketDetail.openMaps') }}
                </a>
            </div>
          </CCol>
        </CRow>

        <CRow v-if="getMachineNumberImage()" class="mb-4">
          <CCol>
            <label class="lbl">{{ $store.state.locale.lang === 'en' ? 'Machine number / asset number photo' : 'รูปหมายเลขเครื่อง / เลขครุภัณฑ์' }}</label>
            <div class="text-center bg-light p-2 rounded border" style="cursor: pointer" @click="openImage(getMachineNumberImage())">
              <img :src="getImageUrl(getMachineNumberImage())" class="img-fluid rounded" style="max-height: 250px" @error="handleImageError" />
            </div>
          </CCol>
        </CRow>

        <CRow class="mb-3">
          <CCol>
            <label class="lbl">{{ $t('ticketDetail.problem') }}</label>
            <div class="p-3 bg-light rounded border" v-html="getProblemDetails()"></div>
          </CCol>
        </CRow>

        <CRow v-if="getProblemImage()" class="mb-4">
          <CCol>
            <label class="lbl">{{ $t('ticketDetail.photoBefore') }}</label>
            <div class="text-center bg-light p-2 rounded border" style="cursor: pointer" @click="openImage(getProblemImage())">
              <img :src="getImageUrl(getProblemImage())" class="img-fluid rounded" style="max-height: 250px" @error="handleImageError" />
            </div>
          </CCol>
        </CRow>

        <div v-if="['done', 'welldone'].includes(report.status)">
          <h5 class="font-weight-bold mb-3 mt-5 text-success">{{ $t('ticketDetail.workOutcome') }}</h5>
          
          <CRow class="mb-3">
             <CCol>
                <label class="lbl">{{ $t('ticketDetail.fixDetail') }}</label>
                <div class="p-3 bg-success-light rounded border border-success">
                   {{ getMaintenanceDetails() || '-' }}
                </div>
             </CCol>
          </CRow>

          <CRow v-if="getMaintenanceImage()" class="mb-4">
            <CCol>
              <label class="lbl text-success">{{ $t('ticketDetail.photoAfter') }}</label>
              <div class="text-center bg-success-light p-2 rounded border border-success" style="cursor: pointer" @click="openImage(getMaintenanceImage())">
                  <img :src="getImageUrl(getMaintenanceImage())" class="img-fluid rounded" style="max-height: 250px" @error="handleImageError" />
              </div>
            </CCol>
          </CRow>
        </div>

      </div>

      <CModal :show.sync="imageModal" centered size="lg">
        <img :src="selectedImageUrl" class="img-fluid w-100" />
        <template #footer><CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') }}</CButton></template>
      </CModal>
    </CCardBody>
  </CCard>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import reportDetailMixin from "../../i18n/reportDetailMixin";
import { formatAvailableTimeDisplay } from "../../i18n/formatAvailableTime";

export default {
  name: "SupervisorReportDetail",
  mixins: [reportDetailMixin],
  props: {
    report: { type: Object, required: true },
  },
  data: () => ({
    imageModal: false,
    selectedImageUrl: null,
    rating: 5,
    hoverRating: 0,
    isSubmittingRating: false,
    technicians: [], 
    selectedTechnicianId: null,
    allCategories: [],
    selectedForwardCategoryId: null,
    showForwardBox: false,
    isProcessing: false,
    showAssignBox: false,
    map: null,
    marker: null,
  }),
  computed: {
    ...mapGetters("auth", ["profile"]),
    // คำนวณว่า supervisor เป็นผู้แจ้งเองหรือไม่
    amIReporter() {
      if (!this.profile || !this.report) return false;
      const myId = String(this.profile._id);
      const reporterId = this.report.userPart?.user?._id
        ? String(this.report.userPart.user._id)
        : this.report.userPart?.user
          ? String(this.report.userPart.user)
          : null;
      return myId === reporterId;
    },
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation;
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude),
        };
      }
      return { lat: 0, lng: 0 };
    },
  },
  watch: {
    report: {
      deep: true,
      handler(val) {
        if (this.isOutdoor()) {
          this.$nextTick(() => {
            this.initMap();
          });
        }
      }
    }
  },
  created() {
    if (this.report.status === "welldone" && this.report.rating) {
      this.rating = this.report.rating.stars;
    }
    if (['pending', 'assigned'].includes(this.report.status)) {
        this.fetchTechnicians();
    }
    this.fetchAllCategories();
  },
  mounted() {
    if (this.isOutdoor()) {
      this.$nextTick(() => {
        this.initMap();
      });
    }
  },
  beforeDestroy() {
    if (this.map) {
      this.map = null;
      this.marker = null;
    }
  },
  methods: {
    async fetchTechnicians() {
        try {
            let url = `${process.env.VUE_APP_APP_URL}/user/technicians`;
            if (this.profile && this.profile.department) {
                const deptId = typeof this.profile.department === 'object' ? this.profile.department._id : this.profile.department;
                url = `${process.env.VUE_APP_APP_URL}/user/technicians/department/${deptId}`;
            }

            const response = await axios.get(url); 
            if(response.data.success) {
                this.technicians = response.data.data;
            }
        } catch (error) {
            console.error("Error fetching technicians:", error);
        }
    },

    async assignTask() {
        if (!this.selectedTechnicianId) return;

        const selectedTech = this.technicians.find(tech => tech._id === this.selectedTechnicianId);
        const techName = selectedTech
          ? `${selectedTech.name || ''} ${selectedTech.surname || ''}`.trim()
          : this.$t('ticketDetail.selectedTechFallback');
        const isChanging = this.report.status !== 'pending';
        const result = await Swal.fire({
            title: isChanging ? this.$t('ticketDetail.confirmChangeTechTitle') : this.$t('ticketDetail.confirmAssignTitle'),
            html: isChanging
              ? this.$t('ticketDetail.confirmChangeTechHtml').replace('{name}', techName)
              : this.$t('ticketDetail.confirmAssignTechHtml').replace('{name}', techName),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#145654',
            confirmButtonText: this.$t('mgmt.confirm'),
            cancelButtonText: this.$t('mgmt.cancel')
        });

        if (result.isConfirmed) {
            this.isProcessing = true;
            try {
                const response = await axios.post(`${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/assign`, {
                    technicianId: this.selectedTechnicianId,
                    supervisorId: this.profile._id 
                });

                if (response.data.success) {
                    Swal.fire(this.$t('mgmt.success'), isChanging ? this.$t('ticketDetail.changeTechOk') : this.$t('ticketDetail.assignTechOk'), 'success');
                    this.showAssignBox = false;
                    this.$emit('refresh'); 
                    this.$emit('close');   
                } else {
                    throw new Error(response.data.message || 'Failed to update ticket');
                }
            } catch (error) {
                console.error(error);
                Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.assignTechFail'), 'error');
            } finally {
                this.isProcessing = false;
            }
        }
    },

    getTechnicianName() {
        const tech = this.report.supervisorPart?.assignedTechnician;
        if (!tech) return this.$t('ticketDetail.techNotYet');
        if (typeof tech === 'object') {
          const n = `${tech.name || ''} ${tech.surname || ''}`.trim();
          return n || this.$t('ticketDetail.noName');
        }
        return this.$t('ticketDetail.noName');
    },
    getRejectionAuthor() {
        const author = this.report.rejectionPart?.rejectedBy;
        if (!author) return "";
        if (typeof author === 'object') {
          const n = `${author.name || ''} ${author.surname || ''}`.trim();
          return n || this.$t('ticketDetail.noName');
        }
        return "";
    },
    getSupervisorName() {
        const sup = this.report.supervisorPart?.supervisor;
        if (!sup) return `${this.profile.name} ${this.$t('ticketDetail.youMarker')}`;
        if (typeof sup === 'object') return `${sup.name || ''} ${sup.surname || ''}`.trim();
        return "-";
    },

    toggleAssignBox() {
      if (this.technicians.length === 0) {
        this.fetchTechnicians();
      }
      this.showAssignBox = !this.showAssignBox;
      this.showForwardBox = false;
    },

    toggleForwardBox() {
      this.showForwardBox = !this.showForwardBox;
      this.showAssignBox = false;
    },

    async fetchAllCategories() {
       try {
           const res = await axios.get(`${process.env.VUE_APP_APP_URL}/category`);
           if (res.data.success) {
               this.allCategories = res.data.data;
           }
       } catch (err) {
           console.error("Error fetching categories:", err);
       }
    },

    isCurrentCategory(catId) {
        if (!this.report.userPart || !this.report.userPart.category) return false;
        const currentCat = this.report.userPart.category;
        const currentId = typeof currentCat === 'object' ? currentCat._id : currentCat;
        return String(catId) === String(currentId);
    },

    async forwardTicket() {
        if (!this.selectedForwardCategoryId) return;
        
        const result = await Swal.fire({
            title: this.$t('ticketDetail.supervisor.forwardConfirmTitle'),
            text: this.$t('ticketDetail.supervisor.forwardConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ffc107',
            cancelButtonColor: '#6c757d',
            confirmButtonText: this.$t('mgmt.confirm'),
            cancelButtonText: this.$t('mgmt.cancel')
        });

        if (result.isConfirmed) {
            this.isProcessing = true;
            try {
                await axios.post(`${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/forward`, {
                    supervisorId: this.profile._id,
                    newCategoryId: this.selectedForwardCategoryId
                });
                
                Swal.fire({ title: this.$t('mgmt.success'), text: this.$t('ticketDetail.supervisor.forwardOk'), icon: 'success' });
                this.showForwardBox = false;
                this.$emit('refresh');
                this.$emit('close'); 
            } catch (error) {
                console.error(error);
                Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.supervisor.forwardFail'), 'error');
            } finally {
                this.isProcessing = false;
            }
        }
    },
    
    async cancelTicket() {
      const result = await Swal.fire({
        title: this.$t('ticketDetail.cancelTicketTitle'),
        html: this.$t('ticketDetail.cancelTicketHtml'),
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: this.$t('ticketDetail.cancelPlaceholder'),
        inputAttributes: {
          'aria-label': this.$t('ticketDetail.cancelAria')
        },
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: this.$t('ticketDetail.cancelConfirmBtn'),
        cancelButtonText: this.$t('ticketDetail.closeBtn'),
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return this.$t('ticketDetail.cancelReasonRequired');
          }
        }
      });

      if (result.isConfirmed) {
        this.isProcessing = true;
        try {
          const response = await axios.put(`${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/cancel`, {
            userId: this.profile._id, 
            cancelReason: result.value
          });
          
          if (response.data.success) {
            Swal.fire(this.$t('mgmt.success'), this.$t('ticketDetail.cancelOk'), 'success');
            this.$emit('refresh');
            this.$emit('close');
          } else {
            throw new Error(response.data.message || 'Failed to cancel ticket');
          }
        } catch (error) {
          console.error('Cancel ticket error:', error);
          Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.cancelFail'), 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },

    async submitEvaluation() {
      if (!this.rating || this.rating < 1) return;
      this.isSubmittingRating = true;
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_APP_URL}/ticket/${this.report._id}/rating`,
          { stars: this.rating }
        );
        if (res.data.success || res.status === 200) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('ticketDetail.swalEvalOk'),
            confirmButtonColor: '#2ba9a5',
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh');
          this.$emit('close');
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: 'error', title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.swalSaveFail') });
      } finally {
        this.isSubmittingRating = false;
      }
    },
    // 🔥 Update Logic Scale (0-100)
    getCurrentStep() {
      const map = { pending: 1, assigned: 2, in_progress: 3, done: 4, welldone: 4 };
      return map[this.report.status] || 1;
    },
    getTimelineStyle() {
      const p = { 1: 0, 2: 33, 3: 66, 4: 100 }[this.getCurrentStep()] || 0;
      return { background: `linear-gradient(to right, #007bff ${p}%, #e0e0e0 ${p}%)` };
    },
    getImageUrl(f) {
      if (!f) return "";
      const filename = f.includes("/") ? f.split("/").pop() : f;
      return `${process.env.VUE_APP_APP_URL}/uploads/${encodeURIComponent(filename)}`;

    },
    openImage(img) {
      this.selectedImageUrl = this.getImageUrl(img);
      this.imageModal = true;
    },
    handleImageError(e) { console.error("Image failed to load"); },
    getUserName() {
      return this.report.userPart?.user ? `${this.report.userPart.user.name} ${this.report.userPart.user.surname}` : "-";
    },
    getOnBehalfOf() {
      if (this.report && this.report.onBehalfOf) return this.report.onBehalfOf;
      return this.report && this.report.userPart && this.report.userPart.onBehalfOf;
    },
    getPersonalPhone() { return this.report.userPart?.personalPhone || "-"; },
    getOfficePhone() { return this.report.userPart?.telephoneWork || this.report.userPart?.user?.telephoneWork || "-"; },
    getDepartment() { return this.report.userPart?.category?.departmentName || "-"; },
    getCategoryName() { return this.report.userPart?.subCategory || "-"; },
    getLocationFull() {
      const u = this.report.userPart;
      if (!u) return "-";
      if (u.locationType === "building") {
        let bName = u.buildingLocation?.building?.BUILDINGCODE || null;
        const rName = u.buildingLocation?.roomName || '-';
        if (bName) {
          return this.$t('ticketDetail.locationBuilding').replace('{b}', bName).replace('{r}', rName);
        }
        // Parse roomName (e.g. "test-04") into building / room
        const dashIdx = rName.indexOf('-');
        if (dashIdx > 0) {
          const parsedBuilding = rName.substring(0, dashIdx).trim();
          const parsedRoom = rName.substring(dashIdx + 1).trim();
          return this.$t('ticketDetail.locationBuilding').replace('{b}', parsedBuilding).replace('{r}', parsedRoom);
        }
        return rName;
      }
      return u.locationDetail || this.$t('ticketDetail.noLocationDetail');
    },
    getAvailableTime() {
      const raw = this.report.userPart?.availableTime;
      if (!raw) return "-";
      void this.$store.state.locale.lang;
      const lang = this.$store.state.locale.lang === "en" ? "en" : "th";
      return formatAvailableTimeDisplay(raw, lang) + this.$t("ticketDetail.timeSuffix");
    },
    getProblemDetails() { return this.report.userPart?.problemDetails || "-"; },
    getMachineNumberImage() { return this.report.userPart?.machineNumberImage; },
    getProblemImage() { return this.report.userPart?.problemImage; },
    getMaintenanceImage() { return this.report.technicianPart?.maintenanceImage; },
    getMaintenanceDetails() { return this.report.technicianPart?.maintenanceDetails; },
    formatDate(dateString) {
        if(!dateString) return "-";
        const d = new Date(dateString);
        const loc = this.$store.state.locale.lang === "en" ? "en-US" : "th-TH";
        return d.toLocaleString(loc);
    },
    isOutdoor() {
      return (
        this.report?.userPart?.locationType === "outdoor" &&
        this.report?.userPart?.outdoorLocation?.latitude &&
        this.report?.userPart?.outdoorLocation?.longitude
      );
    },
    initMap() {
      if (!this.isOutdoor() || !this.$refs.mapContainer) return;
      
      // ตรวจสอบว่า Google Maps API โหลดแล้วหรือยัง
      if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
        console.error('Google Maps API is not loaded. Please check script tag in index.html');
        setTimeout(() => this.initMap(), 100);
        return;
      }

      const position = { lat: this.mapPosition.lat, lng: this.mapPosition.lng };
      
      // สร้างแผนที่ Google Maps
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: position,
        zoom: 16,
        mapTypeId: google.maps.MapTypeId.HYBRID
      });

      // เพิ่ม marker
      this.marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: this.$t('ticketDetail.mapPinTitle')
      });
    }
  },
};
</script>

<style scoped>
.action-box {
  background-color: #fff;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.bg-success-light {
  background-color: #d4edda;
  color: #155724;
}

.lbl {
  font-weight: 700;
  color: #495057;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Timeline CSS */
.work-order-timeline {
  position: relative;
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}

.timeline-line {
  position: absolute;
  top: 15px;
  left: 12%;
  right: 12%;
  height: 3px;
  z-index: 0;
  background: #e0e0e0;
}

.timeline-step {
  position: relative;
  z-index: 1;
  text-align: center;
  width: 25%;
}

.step-circle {
  width: 30px;
  height: 30px;
  background: #fff;
  border: 4px solid #e0e0e0;
  border-radius: 50%;
  margin: 0 auto 5px;
}

.active .step-circle {
  border-color: #007bff;
  background: #007bff;
}

.step-status {
  font-weight: bold;
  font-size: 0.85rem;
  color: #000000; 
}

.step-role {
  font-size: 0.75rem;
  color: #888;
}

/* ... ส่วนอื่นๆ คงเดิม ... */
.evaluation-box { background-color: #fff8e1; border: 2px dashed #ffc107; border-radius: 10px; }
.assignment-box { background-color: #f0f9ff; border: 2px solid #17a2b8; border-radius: 8px; }
.star-rating { font-size: 2.5rem; line-height: 1; cursor: default; }
.star { color: #e4e5e9; transition: color 0.2s; margin: 0 5px; }
.star.filled { color: #ffc107; }

@media (max-width: 768px) {
  .step-circle { width: 20px; height: 20px; border-width: 2px; }
  .timeline-line { top: 10px; }
  .step-status { font-size: 0.7rem; }
  .step-role { display: none; }
  .map-container, img.img-fluid { width: 100% !important; height: auto !important; min-height: 250px; }
}

:deep(.gm-style) {
  height: 100%;
  width: 100%;
  z-index: 0;
}
</style>
